import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Assignment, Event, Warning } from "@mui/icons-material";
import { useSites } from "@/api/hooks/sites";
import type { SiteListRow } from "@/api/types";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function WorklistPage() {
  const { data, isLoading, error } = useSites();

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load worklist: {error.message}</Alert>
    );
  }

  const sites = data ?? [];
  const withOpenActions = sites
    .filter((s) => s.open_action_items > 0)
    .sort((a, b) => b.open_action_items - a.open_action_items);
  const withHighSeverityRisks = sites
    .filter((s) => s.high_severity_open_risks > 0)
    .sort((a, b) => b.high_severity_open_risks - a.high_severity_open_risks);
  const withUpcomingDates = sites
    .filter((s) => s.next_key_date !== null)
    .sort((a, b) =>
      (a.next_key_date as string).localeCompare(b.next_key_date as string),
    );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Worklist</Typography>
        <Typography variant="body2" color="text.secondary">
          Open items across all sites — surfaced from transcript extractions.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
        }}
      >
        <WorklistColumn
          title="Open action items"
          icon={<Assignment color="primary" />}
          empty="No open action items."
          rows={withOpenActions}
          primary={(s) => `${s.open_action_items} open`}
        />
        <WorklistColumn
          title="High-severity risks"
          icon={<Warning color="error" />}
          empty="No open high-severity risks."
          rows={withHighSeverityRisks}
          primary={(s) => `${s.high_severity_open_risks} open`}
          primaryColor="error.main"
        />
        <WorklistColumn
          title="Upcoming key dates"
          icon={<Event color="primary" />}
          empty="No upcoming key dates."
          rows={withUpcomingDates}
          primary={(s) => formatDate(s.next_key_date)}
        />
      </Box>
    </Box>
  );
}

function WorklistColumn({
  title,
  icon,
  empty,
  rows,
  primary,
  primaryColor,
}: {
  title: string;
  icon: React.ReactNode;
  empty: string;
  rows: SiteListRow[];
  primary: (s: SiteListRow) => string;
  primaryColor?: string;
}) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {icon}
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Chip size="small" label={rows.length} />
        </Box>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {empty}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {rows.map((s) => (
              <Link
                key={s.id}
                to={`/clinops/sites/${s.id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography sx={{ flex: 1, fontWeight: 500 }}>
                    {s.common_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={primaryColor ?? "text.primary"}
                    sx={{ fontWeight: 600 }}
                  >
                    {primary(s)}
                  </Typography>
                </Box>
              </Link>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
